import { inject } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, withLatestFrom, switchMap, tap, mergeMap } from 'rxjs/operators';
import { SearchType } from '@ui/aos-lib';

import { ListingPageActions } from './listing-page.actions';
import { ListingPageState } from './listing-page.state';
import { selectListingFeatureState } from './listing-page.reducer';
import { ListingPageModelRegistry } from '../services/listing-page-model/listing-page-model-registry.service';
import { QueryParamsKeys } from '../services/query-params-handler/query-params-handler.types';
import {
  DeviceListingFieldName,
  EquipmentListingFieldName,
  Filter
} from 'src/app/pages/equipment/devices/components/devices.models';
import { mapFilterModelsToFilters } from '../helpers/filters.helper';
import { getInitialFilters } from '../helpers/query-params-filters.helper';
import { SearchState, SortingState } from '../listing-page.types';
import { FilterModels, ListingPageModel } from '../services/listing-page-model/listing-page-model.types';
import { ExportDataService } from 'src/app/services/file/export-data.service';
import { UserConfigService } from 'src/app/services/utility/user-config.service';
import { resolveDateRangeFromPreset } from 'src/app/shared/components/datepicker/datepicker-preset.utils';

function getInitialSearch(
  route: ActivatedRoute,
  currentStoreState: SearchState | null | undefined,
  pageModel: ListingPageModel
): SearchState {
  const cachedSearchValue = route.snapshot.queryParamMap.get(QueryParamsKeys.SearchValue);
  const cachedSearchTypes = route.snapshot.queryParamMap.get(QueryParamsKeys.SearchTypes);
  if (cachedSearchValue && cachedSearchTypes) {
    return {
      searchTypes: cachedSearchTypes.split(',') as DeviceListingFieldName[],
      searchValue: cachedSearchValue,
      isSearched: false
    };
  }
  if (currentStoreState?.searchValue) {
    return currentStoreState;
  }
  return {
    searchTypes: pageModel.selectedSearchType?.().types,
    searchValue: null!,
    isSearched: false
  };
}

function getInitialSearchType(
  route: ActivatedRoute,
  pageModel: ListingPageModel,
  currentStoreSearchType?: SearchType | null
): SearchType {
  const cachedSearchTypes = route.snapshot.queryParamMap.get(QueryParamsKeys.SearchTypes);
  if (cachedSearchTypes) {
    const cachedSearchTypesFound = pageModel.searchTypeList().find(searchType => {
      const stateSearchTypeSet = new Set(cachedSearchTypes.split(',') as EquipmentListingFieldName[]);
      return searchType.types.every(type => stateSearchTypeSet.has(type));
    });
    if (cachedSearchTypesFound) {
      return cachedSearchTypesFound;
    }
  }
  if (currentStoreSearchType) {
    return currentStoreSearchType;
  }
  return pageModel.selectedSearchType();
}

function getInitialSorting(pageModel: ListingPageModel): SortingState {
  return pageModel?.sorting();
}

function getRecoveredStateFiltersFromPreviousState(
  filterModels: FilterModels,
  previousStateFilters: Filter[]
): Filter[] {
  const defaultFilters = mapFilterModelsToFilters(filterModels);
  if (!previousStateFilters.length) {
    return defaultFilters;
  }
  const filtersMap = new Map(previousStateFilters.map(filter => [filter.filterType, filter.filteredValues]));
  const presetIdMap = new Map(previousStateFilters.filter(f => f.presetId).map(f => [f.filterType, f.presetId!]));
  return filterModels.reduce((acc: Filter[], { filterType, isDatepicker, items, isDateRangeSelectable }) => {
    const availableFilteredValues = items.map(item => item.value) as string[];
    const selectedFilteredValues: string[] = filtersMap.get(filterType) || [];
    if (isDatepicker || isDateRangeSelectable) {
      const presetId = presetIdMap.get(filterType);
      const resolved = presetId ? resolveDateRangeFromPreset(presetId) : null;
      return [
        ...acc,
        {
          filterType,
          filteredValues: resolved
            ? [resolved.start.toISOString(), resolved.end.toISOString()]
            : selectedFilteredValues,
          ...(presetId && { presetId })
        }
      ];
    }
    const recoveredValues = selectedFilteredValues.filter(value => availableFilteredValues.includes(value));
    return [
      ...acc,
      {
        filterType,
        filteredValues:
          selectedFilteredValues.length > 0 && recoveredValues.length === 0 ? availableFilteredValues : recoveredValues
      }
    ];
  }, []);
}

function compareFilters(previous: Filter[], current: Filter[]): boolean {
  if (previous.length !== current.length) {
    return false;
  }
  const currentFilterMap = new Map(current.map(f => [f.filterType, new Set(f.filteredValues)]));
  return previous.every(f => {
    const currentFilteredValues = currentFilterMap.get(f.filterType);
    if (!currentFilteredValues || currentFilteredValues.size !== f.filteredValues.length) {
      return false;
    }
    return f.filteredValues.every(value => currentFilteredValues.has(value));
  });
}

function getRefreshedFiltersValidatedAgainstModels(currentFilters: Filter[], filterModels: FilterModels): Filter[] {
  const currentFiltersMap = new Map(currentFilters.map(f => [f.filterType, f.filteredValues]));
  return filterModels.map(model => {
    const currentFilteredValues = currentFiltersMap.get(model.filterType) || [];
    if (model.isDatepicker || model.isDateRangeSelectable) {
      return {
        filterType: model.filterType,
        filteredValues: currentFilteredValues
      };
    }
    const availableValues = model.items.map(item => item.value as string);
    const validatedValues = currentFilteredValues.filter(value => availableValues.includes(value));
    return {
      filterType: model.filterType,
      filteredValues: validatedValues.length > 0 ? validatedValues : availableValues
    };
  });
}

export const gridDataFetched$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), registry = inject(ListingPageModelRegistry)) =>
    actions$.pipe(
      ofType(ListingPageActions.fetchGridData),
      withLatestFrom(store.select(selectListingFeatureState)),
      switchMap(([{ pageId, selection }, listingFeatureState]) => {
        const { payload }: ListingPageState = listingFeatureState[pageId];
        const pageModel = registry.get(pageId);
        const enrichedPayload = selection ? { ...payload!, selection } : payload!;

        return pageModel!.dataSource.getAll(enrichedPayload).pipe(
          switchMap(gridData => of(ListingPageActions.fetchGridDataSuccess({ pageId, gridData }))),
          catchError(() => of(ListingPageActions.fetchGridDataFailure({ pageId })))
        );
      })
    ),
  { functional: true }
);

export const filterModelsFetched$ = createEffect(
  (actions$ = inject(Actions), registry = inject(ListingPageModelRegistry)) =>
    actions$.pipe(
      ofType(ListingPageActions.fetchFilterModels, ListingPageActions.refresh),
      switchMap(({ type, pageId, location }) => {
        const pageModel = registry.get(pageId);
        return pageModel!.filterSource.getAll().pipe(
          mergeMap(filterModels => [
            ListingPageActions.fetchFilterModelsSuccess({
              pageId,
              filterModels,
              isRefreshing: type === ListingPageActions.refresh.type,
              navigationLocation: location ?? null
            })
          ]),
          catchError(() => of(ListingPageActions.fetchFilterModelsFailure({ pageId })))
        );
      })
    ),
  { functional: true }
);

export const stateInitialized$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    registry = inject(ListingPageModelRegistry),
    route = inject(ActivatedRoute),
    router = inject(Router),
    location = inject(Location),
    userConfigService = inject(UserConfigService)
  ) =>
    actions$.pipe(
      ofType(ListingPageActions.fetchFilterModelsSuccess),
      withLatestFrom(store.select(selectListingFeatureState)),
      switchMap(([{ pageId, filterModels, isRefreshing, navigationLocation }, listingFeatureState]) => {
        const pageModel = registry.get(pageId);
        const { state, defaultPaging, searchType: previousSearchType }: ListingPageState = listingFeatureState[pageId];
        const previousSearch = state?.search;

        return userConfigService.getGridSettings<Filter[]>(pageId).pipe(
          switchMap(gridSettings => {
            const columnsWidth = gridSettings?.columnWidth ?? null;
            const columnsOrder = gridSettings?.columnOrder ?? null;
            const columnsVisibility = gridSettings?.columnVisibility ?? null;
            const restoredPageSize: number | null = gridSettings?.pageSize ?? null;

            if (isRefreshing) {
              return [
                ListingPageActions.stateInitialized({
                  pageId,
                  pageState: {
                    filters: getRefreshedFiltersValidatedAgainstModels(state!.filters, filterModels),
                    search: state!.search,
                    sorting: state!.sorting,
                    paging: { skip: 0, take: state!.paging.take },
                    location: state!.location
                  },
                  searchType: previousSearchType || pageModel!.selectedSearchType(),
                  columnsWidth,
                  columnsOrder,
                  columnsVisibility
                })
              ];
            } else {
              const previousStateFilters = gridSettings?.filters ?? [];
              const recoveredStateFilters = getRecoveredStateFiltersFromPreviousState(
                filterModels,
                previousStateFilters
              );
              const initialStateFilters = getInitialFilters(
                recoveredStateFilters,
                filterModels,
                router.parseUrl(location.path()).queryParamMap
              );
              const hasDriftBetweenPreviousAndInitialFilters = !compareFilters(
                previousStateFilters,
                initialStateFilters
              );

              return [
                ListingPageActions.stateInitialized({
                  pageId,
                  pageState: {
                    filters: initialStateFilters,
                    search: getInitialSearch(route, previousSearch, pageModel!),
                    sorting: getInitialSorting(pageModel!),
                    paging: { skip: 0, take: restoredPageSize ?? defaultPaging.take },
                    location: navigationLocation ?? null
                  },
                  searchType: getInitialSearchType(route, pageModel!, previousSearchType),
                  columnsWidth,
                  columnsOrder,
                  columnsVisibility
                }),
                ...(hasDriftBetweenPreviousAndInitialFilters ? [ListingPageActions.saveFiltersState({ pageId })] : [])
              ];
            }
          })
        );
      })
    ),
  { functional: true }
);

export const exportTableDataToCsv$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    registry = inject(ListingPageModelRegistry),
    exportService = inject(ExportDataService)
  ) =>
    actions$.pipe(
      ofType(ListingPageActions.exportTableDataToCsv),
      withLatestFrom(store.select(selectListingFeatureState)),
      switchMap(([{ pageId }, listingFeatureState]) => {
        const { payload, gridData, columnsVisibility, columnsOrder }: ListingPageState = listingFeatureState[pageId];
        const pageModel = registry.get(pageId)!;
        const exportDataSource = pageModel?.exportCsv?.dataSource;

        return (
          exportDataSource
            ? exportDataSource.export({
                filters: payload!.filters,
                sort: payload!.sort,
                search: payload!.search
              })
            : exportService.exportTableToCsv$(payload!, pageModel, gridData.total, columnsVisibility, columnsOrder)
        ).pipe(
          switchMap(() => of(ListingPageActions.exportTableDataToCsvSuccess({ pageId }))),
          catchError(err => {
            console.error(err);
            return of(ListingPageActions.exportTableDataToCsvFailure({ pageId, error: String(err) }));
          })
        );
      })
    ),
  { functional: true }
);

export const saveFiltersState$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), userConfigService = inject(UserConfigService)) =>
    actions$.pipe(
      ofType(ListingPageActions.saveFiltersState),
      withLatestFrom(store.select(selectListingFeatureState)),
      tap(([{ pageId }, listingFeatureState]) => {
        const filters: Filter[] = listingFeatureState[pageId]?.state?.filters ?? [];
        if (!filters.length) {
          return;
        }
        userConfigService.saveFilters(pageId, filters);
      })
    ),
  { functional: true, dispatch: false }
);
